import { createBrowserRouter, Navigate } from "react-router-dom";
import Authenticate from "./pages/authenticate/Authenticate";
import Login from "./pages/login/Login";
import AuthenticatedRoute from "./components/authenticatedRoute/AuthenticatedRoute";
import UnauthenticatedRoute from "./components/authenticatedRoute/UnauthenticatedRoute";
import AuthenticateMobile from "./pages/authenticateMobile/AuthenticateMobile";
import AdminUsersPage from "./pages/admin/Users";
import UserManagementPage from "@pages/admin/UserManagement";
import AuditLogsPage from "@pages/admin/AuditLogs";
import DebugPage from "./pages/admin/Debug";
import ModelPage from "./pages/admin/Model";
import TrainedIndicatorsPage from "@pages/admin/TrainedIndicators";
import { ProductSetupPage } from "@pages/product-setup/ProductSetup";
import { EditSoilSample } from "@pages/soil-samples/EditSoilSample";
import { UserRole } from "@modules/authentication/roles";
import { Routes } from "~/routes";
import { SoilSamplesList } from "@pages/soil-samples/SoilSamplesList";
import { PDFExport } from "@pages/pdf-export/PDFExport";

import NotFoundPage from "./404";
import { AppNotAvailablePage } from "@pages/app-not-available/AppNotAvailable";
import PesTests from "@pages/admin/pes-tests/PesTests";
import PesTestDetails from "@pages/admin/pes-tests/ViewPESTests";
import ComparePesTests from "@pages/admin/pes-tests/ComparePesTests";
import Dashboard from "@pages/admin/Dashboard";
import Customers from "@pages/admin/customers/Customers";
import Cassettes from "@pages/admin/cassettes/Cassettes";
import ViewCassette from "@pages/admin/cassettes/ViewCassette";
import TROManagement from "@pages/admin/tros/TROManagement";
import LocalisationPage from "@pages/admin/Localisation";
import SuperAdminLocalisationPage from "@pages/admin/SuperAdminLocalisation";
import FeatureCacheAdminPage from "@pages/admin/FeatureCacheAdmin";
import FirmwarePage from "@pages/admin/firmware/Firmware";
import UserDashboard from "@pages/user/Dashboard";
import SingleResult from "@pages/user/SingleResult";
import CompareTests from "@pages/user/CompareTests";
import { useCurrentUser } from "@modules/authentication/useCurrentUser";
import ProfileSettingsPage from "@pages/profile/ProfileSettings";

const HomeRedirect = () => {
  const { currentUser, loading } = useCurrentUser();

  if (loading) {
    return null;
  }

  if (currentUser?.role === UserRole.User) {
    return <Navigate to={Routes.AppNotAvailable} replace />;
  }

  if (
    currentUser?.role === UserRole.Admin ||
    currentUser?.role === UserRole.SuperAdmin
  ) {
    return <Navigate to={Routes.AdminDashboard} replace />;
  }

  return null;
};

export const router = createBrowserRouter([
  {
    path: Routes.Home,
    element: (
      /*<AuthenticatedRoute>
        <Link to={getRoute(Routes.Demo)}>Go to demo</Link>
      </AuthenticatedRoute>*/
      <AuthenticatedRoute
        allowedRoles={[UserRole.User, UserRole.Admin, UserRole.SuperAdmin]}
      >
        <HomeRedirect />
      </AuthenticatedRoute>
    ),
    index: true,
  },
  {
    path: Routes.Admin,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <Navigate to={Routes.AdminDashboard} replace />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.Debug,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.SuperAdmin]}>
        <DebugPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.Model,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <ModelPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.Authenticate,
    element: <Authenticate />,
  },
  {
    path: Routes.Login,
    element: (
      <UnauthenticatedRoute>
        <Login />
      </UnauthenticatedRoute>
    ),
  },
  {
    path: Routes.AuthenticateMobile,
    element: <AuthenticateMobile />,
  },
  {
    path: Routes.ProfileSettings,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <ProfileSettingsPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.ProductSetup,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <ProductSetupPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.AdminFirmware,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <FirmwarePage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.SoilSamples,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <SoilSamplesList />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.SoilSampleCreate,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <EditSoilSample mode="create" />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.SoilSampleDetails,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <EditSoilSample mode="edit" />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.AdminDashboard,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <Dashboard />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.UserDashboard,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <UserDashboard />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.UserTestDetails,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <SingleResult />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.UserTestCompare,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <CompareTests />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.Customers,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <Customers />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.AdminCassettes,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <Cassettes />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.AdminCassetteDetails,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <ViewCassette />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.AdminTROs,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <TROManagement />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.AdminUsers,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <AdminUsersPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.UserManagement,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.SuperAdmin]}>
        <UserManagementPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.AuditLogs,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.SuperAdmin]}>
        <AuditLogsPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.FeatureCacheAdmin,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.SuperAdmin]}>
        <FeatureCacheAdminPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.AdminLocalisation,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <LocalisationPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.SuperAdminLocalisation,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.SuperAdmin]}>
        <SuperAdminLocalisationPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.PDFExport,
    element: <PDFExport />,
  },
  {
    path: Routes.TrainedIndicators,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.SuperAdmin]}>
        <TrainedIndicatorsPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.PesTests,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <PesTests />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.PesTestDetails,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <PesTestDetails />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.ComparePesTests,
    element: (
      <AuthenticatedRoute allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
        <ComparePesTests />
      </AuthenticatedRoute>
    ),
  },
  {
    path: Routes.AppNotAvailable,
    element: (
      <AuthenticatedRoute
        allowedRoles={[UserRole.User, UserRole.Admin, UserRole.SuperAdmin]}
      >
        <AppNotAvailablePage />
      </AuthenticatedRoute>
    ),
  },
  { path: Routes.NotFound, element: <NotFoundPage /> },
  { path: "*", element: <NotFoundPage /> },
]);
